import { useMemo } from 'react';
import { SkeletonLine } from '@/shared/components/atoms/LoadingSkeleton';
import BoostPoolsOverviewTable from '@/shared/components/boost/BoostPoolsOverviewTable';
import BoostPoolTabsAndTable from '@/shared/components/boost/BoostPoolTabsAndTable';
import { CustomNumberFlow } from '@/shared/components/CustomNumberFlow';
import { useAllBoostPools } from '@/shared/hooks/useAllBoostPools';
import { useBoostPoolApy } from '@/shared/hooks/useBoostPoolApy';
import { apyToText } from '@/shared/utils/boost';
import { BoostBackground } from 'packages/liquidity-provision/assets/frames/BoostBackground';
import { AllBoostPoolCards } from 'packages/liquidity-provision/components/Boost/AllBoostPoolCards';
import Layout from '../../components/Layout';

export default function BoostOverview() {
  const { apys, isLoading: apyLoading } = useBoostPoolApy();

  const { boostPools: cachedBoostPools, isLoading: cacheDataLoading } = useAllBoostPools();

  const displayedPools = useMemo(
    () =>
      cachedBoostPools
        ?.filter((pool): pool is typeof pool & { asset: 'Btc' } => pool.asset === 'Btc')
        .sort((a, b) => a.feeTierPips - b.feeTierPips)
        .map((pool) => ({
          ...pool,
          apy: apys?.find((apy) => apy.asset === pool.asset && apy.feeTierPips === pool.feeTierPips)
            ?.apy,
        })) || [],
    [cachedBoostPools, apys],
  );

  const highestApy = useMemo(
    () => Math.max(...displayedPools.map((pool) => pool.apy || 0), 0),
    [displayedPools],
  );

  return (
    <Layout title="Boost Overview">
      <div className="relative z-[2] flex w-full flex-col gap-y-6">
        <BoostBackground className="pointer-events-none absolute top-[-66px] z-[-1] h-[400px] w-full" />

        <div className="flex h-[300px] w-[430px] flex-col items-center gap-y-3.5 self-center text-center font-aeonikMedium">
          <img src="/illustration-boost.png" alt="Boost Bolts Illustration" />

          <div className="text-[32px] leading-[normal] text-cf-light-4">
            <div className="flex items-center gap-2">
              Earn up to{' '}
              {displayedPools.length > 0 && !cacheDataLoading && !apyLoading ? (
                <CustomNumberFlow value={apyToText(highestApy)} />
              ) : (
                <SkeletonLine width={90} />
              )}{' '}
              APY
            </div>
            <div>
              with <span className="text-cf-pink-1">Boost</span>
            </div>
          </div>

          <div className="font-aeonikRegular text-16 leading-[normal] text-cf-light-2">
            Earn native BTC yield with a fully permissionless and hands-off liquidity strategy. No
            active management required.
          </div>
        </div>
        <div className="flex gap-6">
          <AllBoostPoolCards />
        </div>
        <BoostPoolsOverviewTable className="border-0 bg-cf-gray-3" />
        <BoostPoolTabsAndTable blockExplorerUrl={process.env.NEXT_PUBLIC_EXPLORER_URL} />
      </div>
    </Layout>
  );
}
